import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSkillExecutions, useSkillFailures, type SkillRegistryRow } from "@/hooks/useSkillDetail";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Props {
  skill: SkillRegistryRow;
}

export function ObservabilityTab({ skill }: Props) {
  const execs = useSkillExecutions(skill.id, 24);
  const failures = useSkillFailures(skill.id, 24);

  const totalExecs = execs.data?.length ?? 0;
  const avgConfidence =
    totalExecs > 0
      ? (execs.data!
          .map((e) => Number(e.pre_response_confidence ?? 0))
          .reduce((a, b) => a + b, 0) / totalExecs).toFixed(2)
      : "—";
  const decisionCounts: Record<string, number> = {};
  for (const e of execs.data ?? []) {
    const d = e.metacog_decision ?? "unknown";
    decisionCounts[d] = (decisionCounts[d] ?? 0) + 1;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Execuções (24h)</CardTitle>
          <CardDescription>
            {totalExecs} execuções · confidence média {avgConfidence}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(decisionCounts).map(([d, n]) => (
              <Badge key={d} variant="outline">
                {d}: {n}
              </Badge>
            ))}
          </div>
          <div className="max-h-80 overflow-auto space-y-1.5">
            {execs.isLoading && <div className="text-xs text-muted-foreground">Carregando…</div>}
            {!execs.isLoading && totalExecs === 0 && (
              <div className="text-xs text-muted-foreground py-6 text-center">Sem execuções no período.</div>
            )}
            {execs.data?.slice(0, 50).map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-xs border rounded px-2 py-1.5">
                <span className="text-muted-foreground tabular-nums">
                  {formatDistanceToNow(new Date(e.created_at), { addSuffix: true, locale: ptBR })}
                </span>
                <Badge variant="outline" className="text-[10px]">{e.metacog_decision ?? "—"}</Badge>
                {e.pre_response_confidence != null && (
                  <Badge variant="outline" className="text-[10px]">
                    conf {Number(e.pre_response_confidence).toFixed(2)}
                  </Badge>
                )}
                {e.execution_time_ms != null && (
                  <Badge variant="outline" className="text-[10px] ml-auto">{e.execution_time_ms}ms</Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Falhas (24h)</CardTitle>
          <CardDescription>{failures.data?.length ?? 0} falhas registradas em skill_failure_log.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-h-80 overflow-auto space-y-1.5">
            {failures.isLoading && <div className="text-xs text-muted-foreground">Carregando…</div>}
            {!failures.isLoading && (failures.data?.length ?? 0) === 0 && (
              <div className="text-xs text-muted-foreground py-6 text-center">Sem falhas — bom sinal.</div>
            )}
            {failures.data?.map((f) => (
              <div key={f.id} className="border rounded px-2 py-1.5 text-xs space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="destructive" className="text-[10px]">{f.failure_type}</Badge>
                  <span className="text-muted-foreground">
                    {formatDistanceToNow(new Date(f.created_at), { addSuffix: true, locale: ptBR })}
                  </span>
                  {f.task_class && (
                    <Badge variant="outline" className="text-[10px] ml-auto">{f.task_class}</Badge>
                  )}
                </div>
                {f.details && Object.keys(f.details as object).length > 0 && (
                  <pre className="text-[10px] text-muted-foreground line-clamp-3">
                    {JSON.stringify(f.details, null, 0)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
